/**
 * Parsers for different data structures.
 *
 * @module parser
 *
 * @author Stanislav Kalashnik <sk@infomir.eu>
 * @author Yaroslav Surilov <y.surilov@infomir.com>
 */

'use strict';


/**
 * Extract name and extension from file name.
 *
 * @param {string} name - file name for parse
 *
 * @return {Object} parsed file name
 */
exports.getFileNameComponents = function ( name ) {
    var matched = name.match(/^([\S\s]+)\.([^.]+)$/);

    return {
        name:      matched ? matched[1] : '',
        extension: matched ? matched[2] : ''
    };
};


/**
 * Parses the given CUE data.
 *
 * @param {string} text - data to parse
 *
 * @return {Object} parsed data
 */
exports.parseCue = function ( text ) {
    var currentFile  = null,
        currentTrack = null,
        data         = {files: []};

    text.split('\n').forEach(function ( line ) {
        var lineParts, lineCommand, value, parts, field, time;

        line        = line.trim();
        lineParts   = line.split(' ');
        lineCommand = lineParts.shift().toLowerCase();

        // strip command name
        value = line.slice(lineCommand.length).trim();

        // check all
        switch ( lineCommand ) {
            case 'title':
            case 'performer':
            case 'songwriter':
                // strip trailing quotes
                if ( value.charAt(0) === '"' && value.charAt(value.length - 1) === '"' ) {
                    value = value.slice(1, value.length - 1);
                }

                if ( currentTrack ) {
                    currentTrack[lineCommand] = value;
                } else {
                    data[lineCommand] = value;
                }

                break;
            case 'file':
                currentFile = {tracks: []};
                parts = value.split(' ');
                // has additional info
                if ( parts.length > 1 && value.charAt(value.length - 1) !== '"' ) {
                    currentFile.type = parts.pop();
                    // strip type part
                    value = value.slice(0, -currentFile.type.length).trim();
                }
                // strip trailing quotes
                if ( value.charAt(0) === '"' && value.charAt(value.length - 1) === '"' ) {
                    value = value.slice(1, value.length - 1);
                }
                currentFile.name = value;
                data.files.push(currentFile);
                break;
            case 'track':
                currentTrack = {
                    number: lineParts[0],
                    type: lineParts[1],
                    index: []
                };
                currentFile.tracks.push(currentTrack);
                break;
            case 'rem':
                if ( ['GENRE', 'DATE', 'COMMENT', 'PLACE'].indexOf(lineParts[0]) !== -1 ) {
                    value = value.slice(lineParts[0].length).trim();
                    field = lineParts[0].toLowerCase();

                    // strip trailing quotes
                    if ( value.charAt(0) === '"' && value.charAt(value.length - 1) === '"' ) {
                        value = value.slice(1, value.length - 1);
                    }

                    if ( currentTrack === null ) {
                        data[field] = value;
                    } else {
                        currentTrack[field] = value;
                    }
                }
                break;
            case 'index':
                if ( currentTrack !== null ) {
                    time = (lineParts[1] || '00:00:00').split(':');

                    currentTrack.index.push((parseInt(time[0], 10) * 60) + parseInt(time[1], 10));
                }
                break;
        }
    });

    return data;
};


/**
 * Parse the given m3u data.
 *
 * @param {string} text - data to parse
 * @param {Function} breadcrumbs - breadcrumbs instance
 *
 * @return {Object} parsed data
 */
exports.parseM3u = function ( text, breadcrumbs ) {
    var lines       = text.trim().split('\n'),
        currentItem = {},
        data        = {
            strict: false,
            config: {},
            items:  []
        },
        additionalData;

    // remove whitespaces from both sides of each string
    lines = lines.map(Function.prototype.call, String.prototype.trim);
    // remove all empty strings
    lines = lines.filter(Boolean);

    // not empty file
    if ( lines.length > 0 ) {
        // validate header
        if ( lines[0] === '#EXTM3U' ) {
            // correct
            data.strict = true;
            // so remove from further parsing
            lines.shift();
        }

        // main parsing
        lines.forEach(function ( line ) {
            var comment, pivot, optionKey, optionValue;

            // data or comment/option
            if ( line.charAt(0) === '#' ) {
                // comment/option
                // try to extract directive name
                switch ( line.substr(1, 6).toUpperCase() ) {
                    case 'EXTCFG':
                        // raw line: "OPTION = VALUE" or "OPTION.PARAM = VALUE"
                        comment = line.substr(7).trim();
                        // position of the divider
                        pivot = comment.indexOf('=');
                        if ( pivot > 0 ) {
                            // extract and set option with value
                            optionKey = comment.substr(0, pivot).trim();
                            optionValue = comment.substr(pivot + 1).trim();
                            // both parts are present
                            if ( optionKey && optionValue ) {
                                // find sub-options
                                optionKey = optionKey.split('.');
                                // plain or multilevel
                                if ( optionKey.length === 1 ) {
                                    // simple
                                    data.config[optionKey[0]] = optionValue;
                                } else {
                                    // nested
                                    // init the structure
                                    if ( typeof data.config[optionKey[0]] !== 'object' ) { data.config[optionKey[0]] = {}; }
                                    // fill it
                                    data.config[optionKey[0]][optionKey[1]] = optionValue;
                                }
                            }
                        }
                        break;
                    case 'EXTINF':
                        // raw line: "419,Alice In Chains - Rotten Apple"
                        comment = line.substr(8).trim();

                        // here parse additional metadata

                        // position of the divider
                        pivot = comment.indexOf(',');

                        if ( pivot > 0 ) {
                            currentItem.time = comment.match(/^-?\d+/)[0];
                            currentItem.name = comment.substr(pivot + 1).trim();

                            additionalData = comment.substr(0, pivot).trim();
                            additionalData = additionalData.replace(/^-?\d+/, '').trim();

                            if ( additionalData ) {
                                additionalData = additionalData.split(' ').map(function ( value ) {
                                    value    = value.split('=');
                                    value[1] = value[1].slice(1, -1);

                                    return value;
                                });

                                currentItem.additionalData = additionalData;
                            }
                        }
                        break;
                }
            } else {
                currentItem.uri = line;

                if ( line.indexOf('://') === -1 ) {
                    // static file
                    // replace window slashes to unix slashes
                    line = line.replace(/\\/g, '/');
                    // set file extension
                    currentItem.extension = line.split('.').pop().toLowerCase();
                    // set path of the file
                    currentItem.path = breadcrumbs.path + line;
                } else {
                    // remote link
                    // set path
                    currentItem.path = line;
                }
                // extend already collected data with uri
                // name always should be filled with either name or uri
                currentItem.name = currentItem.name || currentItem.uri;
                // append to already found
                data.items.push(currentItem);
                // reset
                currentItem = {};
            }
        });

        if ( data.config ) {
            // correct config value types
            ['VM_IPTV_ENABLED', 'VM_OTT_ENABLED', 'VM_OTT_LOG_ENABLED'].forEach(function ( name ) {
                if ( data.config[name] ) {
                    data.config[name] = ['true', 'yes', '1'].indexOf(data.config[name].toLowerCase()) !== -1;
                }
            });
        }
    }

    return data;
};
